import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { useI18n } from '@/contexts/I18nContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Wrench, Plus, Edit, Trash2, Footprints } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Equipment } from '@/types';
import PageSpinner from '@/components/PageSpinner';
import PackageFeatureGate from '@/components/PackageFeatureGate';
import { CardGridSkeleton, PageHeaderSkeleton } from '@/components/PageSkeletons';
import { billingService, equipmentService } from '@/services/storageService';
import { mapRacketsToEquipment, mapShoesToEquipment } from '@/lib/clubApiMappers';

type EquipmentFormState = {
  type: 'shoes' | 'racket';
  make: string;
  manufacturer: string;
  tension: number;
  stringType: string;
};

type EquipmentFormProps = {
  formData: EquipmentFormState;
  manufacturers: string[];
  onChange: (updates: Partial<EquipmentFormState>) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel: string;
  disableType: boolean;
  submitting?: boolean;
  loadingLabel?: string;
};

const EquipmentForm = ({
  formData,
  manufacturers,
  onChange,
  onSubmit,
  submitLabel,
  disableType,
  submitting,
  loadingLabel,
}: EquipmentFormProps) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <div className="space-y-2">
      <Label>Type</Label>
      <Select
        value={formData.type}
        onValueChange={(v) => onChange({ type: v as EquipmentFormState['type'] })}
        disabled={disableType}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="shoes">Shoes</SelectItem>
          <SelectItem value="racket">Racket</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label htmlFor="manufacturer">Manufacturer *</Label>
      {manufacturers.length > 0 ? (
        <>
          <Select
            value={manufacturers.includes(formData.manufacturer) ? formData.manufacturer : '__custom__'}
            onValueChange={(value) =>
              onChange({ manufacturer: value === '__custom__' ? '' : value })
            }
          >
            <SelectTrigger id="manufacturer">
              <SelectValue placeholder="Select manufacturer" />
            </SelectTrigger>
            <SelectContent>
              {manufacturers.map((maker) => (
                <SelectItem key={maker} value={maker}>
                  {maker}
                </SelectItem>
              ))}
              <SelectItem value="__custom__">Other</SelectItem>
            </SelectContent>
          </Select>
          {!manufacturers.includes(formData.manufacturer) && (
            <Input
              value={formData.manufacturer}
              onChange={(e) => onChange({ manufacturer: e.target.value })}
              placeholder="Enter manufacturer"
              required
            />
          )}
        </>
      ) : (
        <Input
          id="manufacturer"
          value={formData.manufacturer}
          onChange={(e) => onChange({ manufacturer: e.target.value })}
          placeholder="Yonex"
          required
        />
      )}
    </div>

    <div className="space-y-2">
      <Label htmlFor="make">Make/Model *</Label>
      <Input
        id="make"
        value={formData.make}
        onChange={(e) => onChange({ make: e.target.value })}
        placeholder={formData.type === 'shoes' ? 'Aeronaut' : 'Astrox 99'}
        required
      />
    </div>

    {formData.type === 'racket' && (
      <>
        <div className="space-y-2">
          <Label htmlFor="tension">String Tension (lbs)</Label>
          <Input
            id="tension"
            type="number"
            min="18"
            max="35"
            value={formData.tension}
            onChange={(e) => onChange({ tension: parseInt(e.target.value, 10) || 24 })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="stringType">String Type</Label>
          <Input
            id="stringType"
            value={formData.stringType}
            onChange={(e) => onChange({ stringType: e.target.value })}
            placeholder="BG65"
          />
        </div>
      </>
    )}

    <Button type="submit" className="w-full" disabled={submitting}>
      {submitting ? loadingLabel || submitLabel : submitLabel}
    </Button>
  </form>
);

export default function EquipmentManagement() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentClubRole, activeClubId } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const queryClient = useQueryClient();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [addDots, setAddDots] = useState('.');
  const [saveDots, setSaveDots] = useState('.');
  
  const [formData, setFormData] = useState<EquipmentFormState>({
    type: 'shoes',
    make: '',
    manufacturer: '',
    tension: 24,
    stringType: '',
  });

  const updateFormData = (updates: Partial<EquipmentFormState>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  useEffect(() => {
    if (!isAdding) {
      setAddDots('.');
      return;
    }
    const frames = ['.', '..', '...'];
    let idx = 0;
    setAddDots(frames[idx]);
    const timer = window.setInterval(() => {
      idx = (idx + 1) % frames.length;
      setAddDots(frames[idx]);
    }, 400);
    return () => window.clearInterval(timer);
  }, [isAdding]);

  useEffect(() => {
    if (!isSaving) {
      setSaveDots('.');
      return;
    }
    const frames = ['.', '..', '...'];
    let idx = 0;
    setSaveDots(frames[idx]);
    const timer = window.setInterval(() => {
      idx = (idx + 1) % frames.length;
      setSaveDots(frames[idx]);
    }, 400);
    return () => window.clearInterval(timer);
  }, [isSaving]);

  const resetForm = () => {
    setFormData({
      type: 'shoes',
      make: '',
      manufacturer: '',
      tension: 24,
      stringType: '',
    });
  };

  const handleAddEquipment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.make || !formData.manufacturer) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsAdding(true);
    try {
      if (formData.type === 'shoes') {
        await equipmentService.addShoe({ make: formData.make, manufacturer: formData.manufacturer });
      } else {
        await equipmentService.addRacket({
          make: formData.make,
          manufacturer: formData.manufacturer,
          tension: String(formData.tension || 24),
          string_type: formData.stringType,
        });
      }
      await queryClient.invalidateQueries({ queryKey: ['equipment-shoes'] });
      await queryClient.invalidateQueries({ queryKey: ['equipment-rackets'] });

      toast.success('Equipment added!');
      setIsAddDialogOpen(false);
      resetForm();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to add equipment');
      console.error(err);
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditEquipment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEquipment) return;

    setIsSaving(true);
    try {
      if (selectedEquipment.type === 'shoes') {
        await equipmentService.updateShoe(selectedEquipment.id, {
          make: formData.make,
          manufacturer: formData.manufacturer,
        });
        await queryClient.invalidateQueries({ queryKey: ['equipment-shoes'] });
      } else {
        await equipmentService.updateRacket(selectedEquipment.id, {
          make: formData.make,
          manufacturer: formData.manufacturer,
          tension: String(formData.tension || 24),
          string_type: formData.stringType,
        });
        await queryClient.invalidateQueries({ queryKey: ['equipment-rackets'] });
      }

      toast.success('Equipment updated!');
      setIsEditDialogOpen(false);
      setSelectedEquipment(null);
      resetForm();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update equipment');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteEquipment = (equipment: Equipment) => {
    if (confirm(t('equipment_delete_confirm', 'Are you sure you want to delete this equipment?'))) {
      (async () => {
        try {
          if (equipment.type === 'racket') {
            await equipmentService.deleteRacket(equipment.id);
            await queryClient.invalidateQueries({ queryKey: ['equipment-rackets'] });
          } else {
            await equipmentService.deleteShoe(equipment.id);
            await queryClient.invalidateQueries({ queryKey: ['equipment-shoes'] });
          }
          toast.success('Equipment deleted');
        } catch (err: any) {
          toast.error(err?.message || t('delete_equipment_failed', 'Failed to delete equipment'));
        }
      })();
    }
  };

  const openEditDialog = (equipment: Equipment) => {
    setSelectedEquipment(equipment);
    setFormData({
      type: equipment.type,
      make: equipment.make,
      manufacturer: equipment.manufacturer,
      tension: equipment.tension || 24,
      stringType: equipment.stringType || '',
    });
    setIsEditDialogOpen(true);
  };

  const shoesQuery = useQuery({
    queryKey: ['equipment-shoes'],
    queryFn: () => equipmentService.listShoes(),
    enabled: Boolean(user),
  });

  const racketsQuery = useQuery({
    queryKey: ['equipment-rackets'],
    queryFn: () => equipmentService.listRackets(),
    enabled: Boolean(user),
  });
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedPackageCode = String(
    billingQuery.data?.package || billingQuery.data?.recommended_package || 'package1',
  )
    .trim()
    .toLowerCase();
  const normalizedRole = String(currentClubRole || user?.role || '').trim().toLowerCase();
  const canUpgradePackage = normalizedRole === 'admin';
  const isEquipmentLockedByPackage = resolvedPackageCode === 'package1';

  const shoes = isEquipmentLockedByPackage ? [] : mapShoesToEquipment(shoesQuery.data as any[]);
  const rackets = isEquipmentLockedByPackage ? [] : mapRacketsToEquipment(racketsQuery.data as any[]);
  const manufacturers = React.useMemo(() => {
    const defaults = [
      'Yonex',
      'Li-Ning',
      'Victor',
      'Apacs',
      'Carlton',
      'Ashaway',
      'Babolat',
      'Forza',
      'Kawasaki',
      'RSL',
      'Gosen',
    ];
    const entries = [...shoes, ...rackets]
      .map((item) => (item.manufacturer || '').trim())
      .filter((value) => value);
    return Array.from(new Set([...defaults, ...entries])).sort((a, b) => a.localeCompare(b));
  }, [shoes, rackets]);

  if (!user) {
    return <PageSpinner label="Loading profile..." />;
  }

  if (shoesQuery.isLoading || racketsQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <PageHeaderSkeleton />
        <CardGridSkeleton count={2} className="grid grid-cols-1 lg:grid-cols-2 gap-6" />
      </div>
    );
  }

  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      <div
        className={`space-y-6 animate-fade-in transition-all ${
          isEquipmentLockedByPackage ? 'blur-[6px] pointer-events-none select-none' : ''
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-3">
              <Wrench className="text-primary" />
              My Equipment
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your badminton gear
            </p>
          </div>

          <Dialog open={isAddDialogOpen} onOpenChange={(open) => {
            setIsAddDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus size={20} className="mr-2" />
                Add Equipment
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Equipment</DialogTitle>
              </DialogHeader>
              <EquipmentForm
                formData={formData}
                manufacturers={manufacturers}
                onChange={updateFormData}
                onSubmit={handleAddEquipment}
                submitLabel="Add Equipment"
                loadingLabel={`Adding${addDots}`}
                submitting={isAdding}
                disableType={false}
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shoes */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Footprints size={20} />
                Shoes ({shoes.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {shoes.length > 0 ? (
                shoes.map(shoe => (
                  <div key={shoe.id} className="p-4 bg-muted rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{shoe.name || shoe.make}</p>
                      <p className="text-sm text-muted-foreground">{shoe.manufacturer}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(shoe)}>
                        <Edit size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteEquipment(shoe)}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-6">
                  No shoes added yet
                </p>
              )}
            </CardContent>
          </Card>

          {/* Rackets */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                Rackets ({rackets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {rackets.length > 0 ? (
                rackets.map(racket => (
                  <div key={racket.id} className="p-4 bg-muted rounded-lg flex items-center justify-between">
                    <div>
                      <p className="font-medium">{racket.name || racket.make}</p>
                      <p className="text-sm text-muted-foreground">{racket.manufacturer}</p>
                      {(racket.tension || racket.stringType) && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {racket.tension && `${racket.tension} lbs`}
                          {racket.tension && racket.stringType && ' | '}
                          {racket.stringType}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(racket)}>
                        <Edit size={16} />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteEquipment(racket)}>
                        <Trash2 size={16} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground text-center py-6">
                  No rackets added yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Edit Dialog */}
        <Dialog
          open={isEditDialogOpen}
          onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) {
              setSelectedEquipment(null);
              resetForm();
            }
          }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Equipment</DialogTitle>
            </DialogHeader>
            <EquipmentForm
              formData={formData}
              manufacturers={manufacturers}
              onChange={updateFormData}
              onSubmit={handleEditEquipment}
              submitLabel="Save Changes"
              loadingLabel={`Saving${saveDots}`}
              submitting={isSaving}
              disableType
            />
          </DialogContent>
        </Dialog>
      </div>

      {isEquipmentLockedByPackage && (
        <div className="absolute inset-0 z-20 bg-background/30 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6">
          <PackageFeatureGate
            featureName="Equipment Management"
            currentPackageCode={resolvedPackageCode}
            canUpgrade={canUpgradePackage}
            onUpgrade={() => navigate('/dashboard/club')}
          />
        </div>
      )}
    </div>
  );
}
